import {Injectable} from "@angular/core";
import {ParameterRule} from "@ispm-client/modules/query-builder/models/query-builder-config";
import {FactorBaseService} from "./factor-base-service";

@Injectable()
export class TherapyService extends FactorBaseService{
  static readonly EFFICACY = "efficacy";
  static readonly EFFICACY_TYPE = "type";
  static readonly REGIMEN = "regimen";
  static readonly DRUGCLASS = "drugclass";
  static readonly DRUG = "drug";
  static readonly THERAPYTIMING = "therapytiming";
  static readonly TIMESINCESTARTVALUE = "timesincestartvalue";
  static readonly TIMESINCEENDVALUE = "timesinceendvalue";
  static readonly TIMESINCEUNITS = "timesinceunits";
  static readonly TREATMENTFIT = "treatmentfit";
  static readonly LINEOFTHERAPY = "lineoftherapy";
  static readonly SETTING = "setting";
  static readonly ABLATIONTYPE = "ablationtype";
  static readonly RESPONSE = "response";
  static readonly THERAPYPHASE = "therapyphase";
  static readonly TOLERANCE = "tolerance";
  static readonly TOLERANCETOXICITY = "tolerancetoxicity";
  static readonly INACTIVETREATMENTTYPE = "inactivetreatmenttype";
  static readonly TRANSPLANTTYPE = "transplanttype";
  static readonly SYSTEMICTHERAPYTYPE = "systemictherapytype";
  static readonly IMMUNOTHERAPYTYPE = "immunotherapytype";
  static readonly SYSTEMICDOSELEVEL = "systemicdoselevel";
  static readonly SYSTEMICDOSEVALUE = "systemicdosevalue";
  static readonly SYSTEMICDOSEUNITS = "systemicdoseunits";
  static readonly SYSTEMICDOSENUMBEROFAGENTS = "systemicdosenumberofagents";
  static readonly SYSTEMICDOSECYCLES = "systemicdosecycles";
  static readonly BESTRESPONSE = "bestresponse";
  static readonly RESPONSEMODALITY = "responsemodality";
  static readonly ROUTE = "route";
  static readonly SURGERYTYPE = "surgerytype";
  static readonly RESIDUALDISEASE = "residualdisease";
  static readonly RESIDUALDISEASEEVIDENCE = "residualdiseaseevidence";
  static readonly RESIDUALTUMOR = "residualtumor";
  static readonly RESECTIONMARGINVALUE = "resectionmarginvalue";
  static readonly RESECTIONMARGINUNITS = "resectionmarginunits";
  static readonly RADIATIONTYPE = "type";
  static readonly DOSERATE = "doserate";
  static readonly DOSEVALUE = "dosevalue";
  static readonly DOSEUNITS = "doseunits";
  static readonly FREQUENCYUNITS = "frequencyunits";
  static readonly FREQUENCYVALUE = "frequencyvalue";
  static readonly NUMBEROFDELIVEREDFRACTIONS = "numberofdeliveredfractions";
  static readonly NUMBEROFESTIMATEDFRACTIONS = "numberofestimatedfractions";
  static readonly PRIMARYSITE = "primarysite";
  static readonly SITE = "site";
  static readonly RADIATIONFRACTIONATION = "radiationfractionation";
  static readonly RADIATIONTOLERANCETOXICITY = "radiationtolerancetoxicity";
  static readonly PATIENT_REFUSES = "Patient Refuses";
  static readonly PATIENT_PREFERS = "Patient Prefers";
  static readonly INTENDED = "Intended";
  static readonly YES = "Yes";



  public getDisplayName(parameterRule: ParameterRule) : string {
    //create a dictionary of Rules where the attribute type is the key (e.g., gene, origin)
    this.parameterRulesMap = {};
    let parameterRules = parameterRule.rules;
    for (let rule of parameterRules) {
      this.parameterRulesMap[rule.field.toLowerCase()] = rule;
    }

    //get therapy type
    let parameter: string = parameterRule.parameter.toLowerCase();
    let therapyType: string = "";
    switch (parameter.toLowerCase()) {
      case "therapyany": {
        therapyType = this.therapyAny();
        break;
      }
      case "therapyablation": {
        therapyType = this.therapyAblation();
        break;
      }
      case "therapychemoradiation": {
        therapyType = this.therapyChemoRadiation();
        break;
      }
      case "therapyefficacy": {
        therapyType = this.therapyEfficacy();
        break;
      }
      case "therapyinactivetreatment": {
        therapyType = this.therapyInactiveTreatment();
        break;
      }
      case "therapyradiation": {
        therapyType = this.therapyRadiation();
        break;
      }
      case "therapytransplant": {
        therapyType = this.therapyStemCellTransplant();
        break;
      }
      case "therapysurgery": {
        therapyType = this.therapySurgery();
        break;
      }
      case "therapysystemic": {
        therapyType = this.therapySystemic();
        break;
      }
    }
    return therapyType;
  }

  private therapyBase(redLines: string): string {
    // {Time Since Start – Value}{Time Since – Units} ‘after start of’
    // {Time Since End – Value}{Time Since – Units} ‘after end of’
    // {Treatment Fit} if ‘Patient Prefers’ or ‘Patient Refuses’
    // {Therapy Timing} if not ‘Intended’
    // {Line of Therapy}
    // {Setting}
    // ‘Therapy’ (if Therapy Any) or use red lines in the other therapy types if non-empty
    // {Treatment Fit} if not ‘Patient Prefers’ or ‘Patient Refuses’
    // ‘, response:’ {Response}

    var results: string[] = new Array();

    this.timeSinceStartValue(results);
    this.timeSinceEndValue(results);
    this.treatmentFitPatientPrefersRefuses(results);
    this.therapyTiming(results);
    this.lineOfTherapy(results);
    this.setting(results);
    results.push(redLines);
    this.treatmentFitNotPatientPrefersRefuses(results);
    this.response(results);

    let delimitedString = results.join(TherapyService.SPACE);
    delimitedString = delimitedString.replace(" ,", ",");
    return delimitedString;
  }

  private therapyAny(): string {
  // {Time Since Start – Value}{Time Since – Units} ‘after start of’
  // {Time Since End – Value}{Time Since – Units} ‘after end of’
  // {Treatment Fit} if ‘Patient Prefers’ or ‘Patient Refuses’
  // {Therapy Timing} if not ‘Intended’
  // {Line of Therapy}
  // {Setting}
  // ‘Therapy’ or use red lines in subsequent therapy types if non-empty
  // {Treatment Fit} if not ‘Patient Prefers’ or ‘Patient Refuses’
  // ‘, response:’ {Response}

    return this.therapyBase("Therapy");
  }

  private therapyAblation(): string {
    // Therapy – Ablation
    // <insert of all Therapy – Any here while using the following red line:>
    // {Ablation Type} or ‘Ablation’

    var results: string[] = new Array();
    this.ablationType(results);
    let delimitedString = results.join(TherapyService.SPACE);
    return this.therapyBase(delimitedString);
  }

  private therapyChemoRadiation(): string {
    // Therapy – Chemoradiation
    // {Tolerance}
    // <insert of all Therapy – Any here while using the following red line:>
    // ‘Chemoradiation’

    var results: string[] = new Array();
    this.toleranceToxicity(results);
    let delimitedString = results.join(TherapyService.SPACE);
    return delimitedString + TherapyService.SPACE + this.therapyBase("Chemoradiation");
  }

  private therapyEfficacy(): string {
    /*
    Therapy – Efficiency
    Do not use Therapy – Any for this one. Just:
    {Efficacy} ‘to’ {Type} {Regimen} {Drug Class} { Drug}
     */

    var results: string[] = new Array();

    this.efficacy(results);
    this.efficacyType(results);
    this.regimen(results);
    this.drugClass(results);
    this.drug(results);

    let delimitedString = results.join(TherapyService.SPACE);
    return delimitedString;
  }

  private therapyInactiveTreatment(): string {
    /*
    Therapy – Inactive Treatment
    <insert of all Therapy – Any here while using the following red line:>
    {Inactive Treatment Type} or ‘Inactive Treatment’
     */
    var results: string[] = new Array();
    this.inactiveTreatmentType(results);
    let delimitedString = results.join(TherapyService.SPACE);
    return this.therapyBase(delimitedString);
  }

  private therapyRadiation(): string {
    /*
    Therapy – Radiation
    {Tolerance}
    <insert of all Therapy – Any here while using the following red line:>
    {Type} or ‘Radiation’
    {Primary Site==Yes?Primary Site}
    {Dose Value}{Dose Units}
    Number of estimated fractions: {Number of estimated fractions}
    Number of delivered fractions: {Number of delivered fractions}
    {Radiation Fractionation}
     */
    var results: string[] = new Array();

    this.radiationToleranceToxicity(results);
    let tolerance = results.join(TherapyService.SPACE);

    results = new Array();
    this.radiationType(results);
    let redLine = results.join(TherapyService.SPACE);

    let delimitedString: string = tolerance + TherapyService.SPACE + this.therapyBase(redLine);

    results = new Array();
    this.primarySite(results);
    this.doseValue(results);
    // this.doseRate(results);
    // this.frequencyValue(results);
    // this.frequencyUnits(results);
    this.numberOfEstimatedFractions(results);
    this.numberOfDeliveredFractions(results);
    this.radiationFractionation(results);

    let delimitedString2 = results.join(TherapyService.SPACE);
    return delimitedString + delimitedString2;
  }

  private therapyStemCellTransplant(): string {
  /*
  Therapy – Stem Cell Transplant
  <insert of all Therapy – Any here while using the following red line:>
  {Transplant Type} ‘Transplant’ or just ‘Transplant’ if empty
   */
    var results: string[] = new Array();
    this.transplantType(results);
    let delimitedString = results.join(TherapyService.SPACE);
    return this.therapyBase(delimitedString);
  }

  private therapySurgery(): string {
    /*
    Therapy – Surgery
    <insert of all Therapy – Any here while using the following red line:>
    {Surgery Type} ‘or just ‘Surgery’ if empty
    Response: {Response}
    Residual Disease: {Residual Disease}
    {Residual Tumor}
    Resection margin: {Resection Margin – Value}{Resection Margin – Units}
     */
    var results: string[] = new Array();
    this.surgeryType(results);
    let delimitedString = results.join(TherapyService.SPACE);
    delimitedString = this.therapyBase(delimitedString);

    this.residualDisease(results);
    //this.residualDiseaseEvidence(results);
    this.residualTumor(results);
    this.resectionMarginValue(results);

    delimitedString = delimitedString + TherapyService.SPACE + results.join(TherapyService.SPACE);
    return delimitedString;
  }

  private therapySystemic(): string {
    /*
    Therapy – Systemic
    {Tolerance}
    <insert of all Therapy – Any here while using the following red line:>
    {Systemic Type}
    {Immunotherapy Type}
    {Regimen}
    {Drug Class}
    Or ‘Systemic Therapy’ of all of the above are empty

    {Route}
    {Systemic Dose Level} ‘dose’ but leave out ‘dose’ when the attribute value already contains ‘dose’
    {Systemic Dose – Value}{Systemic Dose – Units}
    {Cycles} ‘cycles’
    {Number of Agents} ‘agents’
    ‘Response: ‘ {Response}
    {Best Response}
    ‘by’ {Response Modality’
     */
    var results: string[] = new Array();
    this.tolerance(results);
    let delimitedString = results.join(TherapyService.SPACE);

    results = new Array();
    this.systemicTherapyType(results);
    this.immunoTherapyType(results);
    this.regimen(results);
    this.drug(results);
    this.drugClass(results);
    if (results.length === 0) {
      results.push("Systemic Therapy");
    }
    delimitedString = results.join(TherapyService.SPACE);
    delimitedString = this.therapyBase(delimitedString);

    results = new Array();
    this.route(results);
    this.systemicDoseLevel(results);
    this.systemicDoseValue(results);
    this.cycles(results);
    this.numberOfAgents(results);
    //this.response(results);
    this.bestResponse(results);
    this.responseModality(results);

    delimitedString = delimitedString + TherapyService.SPACE + results.join(TherapyService.SPACE);
    return delimitedString;
  }

  private efficacy(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.EFFICACY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
      results.push("to");
    }
  }

  private efficacyType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.EFFICACY_TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private regimen(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.REGIMEN];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private drugClass(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.DRUGCLASS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private drug(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.DRUG];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private therapyTiming(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.THERAPYTIMING];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
      let topOfStack = results.pop();
      if (topOfStack !== TherapyService.INTENDED) {
        results.push(topOfStack);
      }
    }
  }

  private timeSinceStartValue(results: string[]) {
    // {Time Since Start – Value}{Time Since – Units} ‘after start of’
    let rule = this.parameterRulesMap[TherapyService.TIMESINCESTARTVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      //see if we have units
      this.timeSinceUnits(displayValue,results);
      results.push("after start of");
    }
  }

  private timeSinceEndValue(results: string[]) {
    // {Time Since End – Value}{Time Since – Units} ‘after end of’
    let rule = this.parameterRulesMap[TherapyService.TIMESINCEENDVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      //see if we have units
      this.timeSinceUnits(displayValue, results);
      results.push("after end of");
    }
  }

  private timeSinceUnits(value: string, results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TIMESINCEUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (parseInt(value) === 1) {
        displayValue = displayValue.substring(0, displayValue.length-1);
      }
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private treatmentFitPatientPrefersRefuses(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TREATMENTFIT];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
      let topOfStack = results.pop();
      if (topOfStack === TherapyService.PATIENT_PREFERS || topOfStack === TherapyService.PATIENT_REFUSES) {
        results.push(topOfStack);
      }
    }
  }
  private treatmentFitNotPatientPrefersRefuses(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TREATMENTFIT];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
      let topOfStack = results.pop();
      if (topOfStack !== TherapyService.PATIENT_PREFERS && topOfStack !== TherapyService.PATIENT_REFUSES) {
        results.push(topOfStack);
      }
    }
  }

  private treatmentFit(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TREATMENTFIT];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private lineOfTherapy(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.LINEOFTHERAPY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private setting(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SETTING];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private ablationType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.ABLATIONTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push("Ablation");
    }
  }

  private response(results: string[]) {
    // ‘, response:’ {Response}
    let rule = this.parameterRulesMap[TherapyService.RESPONSE];
    if (rule) {
      results.push(", response:");
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private therapyPhase(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.THERAPYPHASE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private toleranceToxicity(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TOLERANCETOXICITY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tolerance(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.TOLERANCE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private inactiveTreatmentType(results: string[]) {
    //{Inactive Treatment Type} or ‘Inactive Treatment’
    let rule = this.parameterRulesMap[TherapyService.INACTIVETREATMENTTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push("Inactive Treatment");
    }
  }

  private transplantType(results: string[]) {
    //{Transplant Type} ‘Transplant’ or just ‘Transplant’ if empty
    let rule = this.parameterRulesMap[TherapyService.TRANSPLANTTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    results.push("Transplant");
  }

  private systemicTherapyType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICTHERAPYTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private route(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.ROUTE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private immunoTherapyType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.IMMUNOTHERAPYTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private systemicDoseLevel(results: string[]) {
    //{Systemic Dose Level} ‘dose’ but leave out ‘dose’ when the attribute value already contains ‘dose’
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICDOSELEVEL];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (!displayValue.endsWith('Dose')) {
        displayValue += ' dose';
      }
      this.pushWithOperator(rule, displayValue, results);

    }
  }

  private systemicDoseValue(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICDOSEVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      this.systemicDoseUnits(results);
    }
  }

  private systemicDoseUnits(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICDOSEUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cycles(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICDOSECYCLES];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      if (parseInt(displayValue) === 1)
        results.push("cycle");
      else
        results.push("cycles");
    }
  }

  private numberOfAgents(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SYSTEMICDOSENUMBEROFAGENTS];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      if (parseInt(displayValue) === 1)
        results.push("agent");
      else
        results.push("agents");
    }
  }

  private bestResponse(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.BESTRESPONSE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private responseModality(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RESPONSEMODALITY];
    if (rule) {
      results.push("by");
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private radiationType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RADIATIONTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push("Radiation");
    }
  }

  private residualDisease(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RESIDUALDISEASE];
    if (rule) {
      results.push("Residual Disease: ");
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private residualDiseaseEvidence(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RESIDUALDISEASEEVIDENCE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private residualTumor(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RESIDUALTUMOR];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private resectionMarginValue(results: string[]) {
    //Resection margin: {Resection Margin – Value}{Resection Margin – Units}
    let rule = this.parameterRulesMap[TherapyService.RESECTIONMARGINVALUE];
    if (rule) {
      results.push("Resection margin: ");
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      this.resectionMarginUnits(results);
    }
  }

  private resectionMarginUnits(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RESECTIONMARGINUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private surgeryType(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SURGERYTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push("Surgery");
    }
  }

  private doseValue(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.DOSEVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      this.doseUnits(results);
    }
  }

  private doseRate(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.DOSERATE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private doseUnits(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.DOSEUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private frequencyValue(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.FREQUENCYVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private frequencyUnits(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.FREQUENCYUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private numberOfDeliveredFractions(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.NUMBEROFDELIVEREDFRACTIONS];
    if (rule) {
      results.push("Number of delivered fractions:");
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private numberOfEstimatedFractions(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.NUMBEROFESTIMATEDFRACTIONS];
    if (rule) {
      results.push("Number of estimated fractions:");
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private primarySite(results: string[]) {
    //{Primary Site==Yes?Primary Site}
    let rule = this.parameterRulesMap[TherapyService.PRIMARYSITE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
      let topOfStack = results.pop();
      if (topOfStack === TherapyService.YES) {
        this.site(results);
      }
    }
  }

  private radiationFractionation(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RADIATIONFRACTIONATION];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private radiationToleranceToxicity(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.RADIATIONTOLERANCETOXICITY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private site(results: string[]) {
    let rule = this.parameterRulesMap[TherapyService.SITE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }


}
